/**
 * TemplateGrid
 *
 * Masonry-like grid for templates with responsive column count and image-load
 * tracking. Evenly distributes items across columns for consistent layout.
 */
import React, { useEffect, useRef, useState } from 'react';
import TemplateCard from './TemplateCard';
import NoResults from './NoResults';
import './MasonryLayout.css';

const TemplateGrid = ({ 
  templates, 
  onTemplateClick, 
  savedTemplateIds = new Set(), 
  onSaveTemplate,
  onImagesLoaded,
  maxColumns = 4, // New prop to control maximum columns (default to 4 for existing behavior)
  isMail = false // Hint grid to compress to 2 columns earlier for mail pieces
}) => {
  const [columns, setColumns] = useState(4); // Default to 4 columns
  const gridRef = useRef(null);
  const [loadedImages, setLoadedImages] = useState(new Set());
  const [allImagesLoaded, setAllImagesLoaded] = useState(false);

  // Set up columns based on screen size
  useEffect(() => {
    const updateColumns = () => {
      const width = window.innerWidth;
      if (isMail) {
        // For mail templates, switch to 2 columns earlier for comfort
        if (width < 1280) {
          setColumns(2);
        } else {
          setColumns(Math.min(3, maxColumns));
        }
        return;
      }
      // Default behavior for non-mail templates
      if (width < 1024) {
        setColumns(2);
      } else if (width < 1280) {
        setColumns(Math.min(3, maxColumns));
      } else {
        setColumns(maxColumns);
      }
    };

    // Initial column setup
    updateColumns();

    // Update columns on resize
    window.addEventListener('resize', updateColumns);
    return () => window.removeEventListener('resize', updateColumns);
  }, [maxColumns, isMail]);

  // Track image loading progress
  useEffect(() => {
    if (templates.length === 0) return;
    
    // Reset loaded images when templates change
    setLoadedImages(new Set());
    setAllImagesLoaded(false);
  }, [templates]); // Only depend on templates changing
  
  // Separate effect to detect when all images are loaded
  useEffect(() => {
    if (templates.length === 0 || loadedImages.size === 0) return;
    
    // If all images are loaded, notify parent
    if (loadedImages.size >= templates.length) {
      setAllImagesLoaded(true);
      if (onImagesLoaded) {
        onImagesLoaded(true);
      }
    }
  }, [loadedImages, templates.length, onImagesLoaded]);

  // Handle image load events from TemplateCard
  const handleImageLoaded = (templateId) => {
    setLoadedImages(prev => {
      const newSet = new Set(prev);
      newSet.add(templateId);
      return newSet;
    });
  };

  if (templates.length === 0) {
    return <NoResults />;
  }

  // Simplified distribute templates across columns
  const distributeTemplates = () => {
    // Create column arrays
    const columnArrays = Array.from({ length: columns }, () => []);
    
    // Simply distribute templates evenly across columns for better layout
    templates.forEach((template, index) => {
      // Add each template to the column based on index modulo columns
      // This ensures even distribution rather than trying to calculate heights
      const columnIndex = index % columns;
      columnArrays[columnIndex].push(template);
    });
    
    return columnArrays;
  };
  
  const columnArrays = distributeTemplates();

  // Convert API data for TemplateCard component
  const mapTemplateForCard = (template) => {
    // Create a title if one doesn't exist (use company or adCopy for fallback)
    const title = template.title || template.company || 
      (template.adCopy ? template.adCopy.split('\n')[0].substring(0, 50) : 'Template');
    
    // Debug video templates
    if (template.type === 'Video') {
      console.log('Video template found:', template.id);
      console.log('Video URL:', template.videoUrl);
    }
    
    // Ensure video URL is properly formatted if it's a video template
    const isVideo = template.type === 'Video' && template.videoUrl;
    const videoUrl = isVideo ? (
      template.videoUrl.startsWith('gs://') 
        ? `https://storage.googleapis.com/${template.videoUrl.substring(5)}` 
        : template.videoUrl
    ) : null;
    
    // Log the processed video URL
    if (isVideo) {
      console.log('Processed video URL:', videoUrl);
    }
    
    // Process data for template card
    const mappedTemplate = {
      ...template,
      // Map API fields to TemplateCard expected fields
      id: template.id,
      title: title,
      // Make sure previewImageURL is explicitly passed through
      previewImageURL: template.previewImageURL,
      // Use different priority for mail templates to ensure front image is default
      imageUrl: template.type?.toLowerCase() === 'mail' ? 
        (template.frontImageUrl || template.previewImageURL || template.imageUrl || template.thumbnailUrl || 'https://via.placeholder.com/300x200') :
        (template.previewImageURL || template.imageUrl || template.thumbnailUrl || 'https://via.placeholder.com/300x200'),
      author: template.company || template.author || 'Unknown',
      // Add saved status
      isSaved: savedTemplateIds.has(template.id),
      // Ensure obfuscated flag is passed through
      obfuscated: !!template.obfuscated,
      // Ensure video-specific properties are passed
      type: template.type,
      videoUrl: videoUrl,
      // Ensure mail template properties are passed through
      frontImageUrl: template.frontImageUrl,
      backImageUrl: template.backImageUrl
    };
    
    return mappedTemplate;
  };

  return (
    <div className="masonry-wrapper">
      <div className="masonry-grid" ref={gridRef}>
        {columnArrays.map((columnTemplates, columnIndex) => (
          <div 
            key={`column-${columnIndex}`} 
            className="masonry-column"
            style={{ width: `${100 / columns}%` }}
          >
            {columnTemplates.map((template) => (
              <div 
                key={template.id} 
                className="masonry-item p-2"
              >
                <div className={`masonry-item-inner ${!template.obfuscated ? 'cursor-pointer' : ''}`}>
                  <TemplateCard 
                    template={mapTemplateForCard(template)} 
                    onTemplateClick={onTemplateClick}
                    onSaveTemplate={onSaveTemplate ? () => onSaveTemplate(template.id, savedTemplateIds.has(template.id)) : undefined}
                    onImageLoaded={() => handleImageLoaded(template.id)}
                  />
                </div>
              </div>
            ))}
          </div>
        ))}
      </div>
    </div>
  );
};

export default TemplateGrid; 